import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/** Merge Tailwind classes safely */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/** Format currency */
export function formatCurrency(amount, currency = "USD") {
  if (amount === null || amount === undefined || amount === "") return "—";
  return new Intl.NumberFormat("es-PY", {
    style: "currency", currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parseFloat(amount) || 0);
}

/** Format date YYYY-MM-DD → "15 may" */
export function formatDate(str) {
  if (!str) return "—";
  try {
    const d = new Date(str + "T12:00:00");
    return d.toLocaleDateString("es-PY", { day: "numeric", month: "short" });
  } catch { return str; }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * FUENTE ÚNICA DE LA VERDAD — espejo de backend src/lib/task-status.js
 * Si cambia algo acá, cambiar allá (y viceversa). NO definir estados inline.
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Normaliza para CLASIFICAR (no altera lo que se muestra/guarda).
 * Minúsculas, sin acentos, espacios colapsados. Mapea alias legacy.
 * Espejo de backend src/lib/task-status.js normEstado().
 */
const ALIAS_ESTADO = { "en-progreso": "en-proceso" };
export function normEstado(estado) {
  let e = String(estado || "")
    .trim().toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ");
  return ALIAS_ESTADO[e] || e;
}

/** ACTIVOS: trabajo en curso (requieren acción, pueden estar atrasadas). */
export const ESTADOS_ACTIVOS = [
  // Monday
  "brief pendiente", "para asignar", "asignado", "en curso",
  "para qc", "qc cambios", "qc aprobado", "ec cambios", "ec aprobado",
  "enviado cliente",                            // jun 2026 v4
  "tarea con cambio",                           // jun 2026 v4 — NUEVO
  "cliente cambios", "ajustar brief", "pendiente de produccion",
  // Internas legacy
  "pendiente", "borrador", "brief", "ajustar-brief", "en-proceso",
  "para-qc-ia", "para-qc", "qc-com-pendiente",
  "revision-interna", "revision-ec", "ec-entregado", "revision-cliente",
];

/** PUBLICADO: contenido publicado / cerrado. */
export const ESTADOS_PUBLICADO = ["publicado", "entregado", "terminado", "publicado-sin-aprobacion"];

/** CERRADOS: tareas histórico, sin trabajo activo (jun 2026 v4). */
export const ESTADOS_CERRADOS = [
  "publicado", "entregado", "terminado",
  "cancelado",
  "archivado", "publicado-sin-aprobacion",
];
const ESTADOS_CERRADOS_SET = new Set(ESTADOS_CERRADOS);
export const isAbierta = (estado) => !ESTADOS_CERRADOS_SET.has(normEstado(estado));
export const isCerrada = (estado) => ESTADOS_CERRADOS_SET.has(normEstado(estado));
export const etapaDe   = (estado) => isCerrada(estado) ? "Cerrada" : "Abierta";

/** PENDIENTE DE PUBLICAR: listo/aprobado pero todavía no publicado. */
export const ESTADOS_PENDIENTE_PUBLICAR = [
  "aprobado cliente", "programado", "aprobado-cliente", "aprobado",
];

/** LISTO (estado general "terminado"): publicado + por publicar + archivado. */
export const ESTADOS_COMPLETADOS = [
  ...ESTADOS_PUBLICADO, ...ESTADOS_PENDIENTE_PUBLICAR, "archivado",
];

/** NO VENCIBLES: todo lo que NO es activo nunca cuenta como atrasada. */
export const ESTADOS_NO_VENCIBLES = [...ESTADOS_COMPLETADOS, "pausa", "cancelado"];

/** Estados de trabajo "en proceso" (subset de activos, excluye sin-empezar). */
export const ESTADOS_EN_PROCESO = [
  "en curso", "para qc", "qc cambios", "qc aprobado", "ec cambios", "ec aprobado",
  "cliente cambios", "ajustar brief", "pendiente de produccion",
  "brief", "ajustar-brief", "en-proceso", "para-qc-ia", "para-qc",
  "qc-com-pendiente", "revision-interna", "revision-ec", "ec-entregado", "revision-cliente",
];

export const isActiva    = (estado) => ESTADOS_ACTIVOS.includes(normEstado(estado));
export const isListo     = (estado) => ESTADOS_COMPLETADOS.includes(normEstado(estado));
export const isPublicado = (estado) => ESTADOS_PUBLICADO.includes(normEstado(estado));
export const isPendientePublicar = (estado) => ESTADOS_PENDIENTE_PUBLICAR.includes(normEstado(estado));

// ─── Mi Día · helpers compartidos back/front (jun 2026) ────────────────────
// Espejo de src/lib/task-status.js en el backend. Si cambia uno, cambiar el otro.
export const ESTADOS_AVANCE = [
  ...ESTADOS_PUBLICADO,
  ...ESTADOS_PENDIENTE_PUBLICAR,
  "archivado",
  "qc aprobado", "ec aprobado",
  "enviado cliente",          // v6.5.17 · EC envió, no es regresión
  "pendiente de produccion",  // v6.5.17 · CCC pasó a producción (CPC)
  "para qc",
];
export const isEnAvance = (estado) => ESTADOS_AVANCE.includes(normEstado(estado));

// Fecha PY (UTC-3) de un timestamp
function _fechaPY(ts) {
  if (!ts) return null;
  try {
    return new Date(new Date(ts).getTime() - 3 * 3600 * 1000).toISOString().slice(0, 10);
  } catch { return null; }
}

// v6.5.19 — Por rol, ciertos estados de "regresión" SÍ son cierre productivo
// Espejo de CIERRE_POR_ROL en src/lib/transicion-cierre.js (backend)
const CIERRE_EXTRA_POR_ROL = {
  EC:           ["ec cambios"],
  COM:          ["qc cambios", "para asignar"],
  CCC:          ["asignado"],
  CPC:          ["asignado"],
};
function _normRolFront(rol) {
  if (!rol) return null;
  const s = String(rol).trim().toUpperCase();
  if (s === "CCC" || s === "EC" || s === "COM" || s === "CPC" || s === "SMM") return s;
  if (/DISE[ÑN]AD|FOTOG|FOTÓG|AUDIOV|EDITOR|CREAT|MIEMBRO/.test(s)) return "especialista";
  return null;
}

// "Completada hoy" — estado en avance Y estado_desde_at PY == fecha
// rol opcional → para roles que cierran con estados de regresión (EC Cambios, etc.)
export function isCompletadoMiDia(t, fecha, rol = null) {
  const e = normEstado(t.estado);
  const enAvance = ESTADOS_AVANCE.includes(e);
  const r = _normRolFront(rol);
  const extra = r && CIERRE_EXTRA_POR_ROL[r] ? CIERRE_EXTRA_POR_ROL[r] : [];
  if (!enAvance && !extra.includes(e)) return false;
  const f = _fechaPY(t.estado_desde_at);
  return f === fecha;
}

// v5.5 — Modelo semántico explícito:
//   fecha_tarea  = día PLANIFICADO de trabajo (Mi Día)
//   fecha_limite = deadline INTERNO (atrasada si < hoy)
//   deadline     = deadline CLIENTE (prioridad)
// v6.5.1 — soportar Date objects además de strings ISO (defensa en profundidad)
const _s = d => {
  if (!d) return null;
  if (d instanceof Date) {
    if (isNaN(d)) return null;
    if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0) {
      const y = d.getUTCFullYear(), m = String(d.getUTCMonth() + 1).padStart(2, "0"), day = String(d.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
    return new Date(d.getTime() - 3 * 3600 * 1000).toISOString().slice(0, 10);
  }
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s.slice(0, 10);
};
export function fechaPlanificada(t)   { return _s(t.fecha_tarea); }
export function fechaLimiteInterna(t) { return _s(t.fecha_limite); }
export function fechaDeadline(t)      { return _s(t.deadline || t.fecha_entrega); }

// "Entra al Mi Día de `fecha`" — v5.5:
//   - completada ese día
//   - fecha_tarea === fecha (planificada para ese día)
//   - atrasada por fecha_limite (debe cerrarse)
// NO entra: sin fecha_tarea ni fecha_limite (esas van a "Por planificar")
export function isPartOfMiDia(t, fecha) {
  const e = normEstado(t.estado);
  if (e === "archivado" || e === "cancelado") return false;
  if (isCompletadoMiDia(t, fecha)) return true;
  if (!isActiva(t.estado)) return false;
  // v8.4.24 · Cualquier estado en ESTADOS_AVANCE representa un pase efectivo:
  //   el responsable ya cumplió, la pelota pasó al siguiente rol / cliente /
  //   producción. No debe marcarse como atrasada ni ocupar Mi Día del user.
  if (isEnAvance(t.estado)) return false;
  const fTarea  = fechaPlanificada(t);
  const fLimite = fechaLimiteInterna(t);
  if (fLimite && fLimite < fecha) return true;        // atrasada
  if (fTarea && fTarea === fecha) return true;        // planificada para hoy
  return false;                                       // futura / sin fecha
}

// "Por planificar" — activa, sin fecha_tarea ni fecha_limite, no completada hoy
export function isPorPlanificar(t) {
  if (!isActiva(t.estado)) return false;
  if (isEnAvance(t.estado)) return false;             // v8.4.24 · ver arriba
  const fTarea  = fechaPlanificada(t);
  const fLimite = fechaLimiteInterna(t);
  return !fTarea && !fLimite;
}

// v5.5 — Prioridad por deadline cliente. Reemplaza el campo manual.
export function prioridadCalculada(t, hoy = hoyPY()) {
  const dl = fechaDeadline(t);
  if (dl) {
    const dias = Math.floor((new Date(dl) - new Date(hoy)) / 86400000);
    let nivel;
    if (dias <= 1)      nivel = "critica";
    else if (dias <= 3) nivel = "alta";
    else if (dias <= 7) nivel = "media";
    else                nivel = "baja";
    return { nivel, dias_a_deadline: dias, source: "deadline" };
  }
  if (t.prioridad) return { nivel: String(t.prioridad).toLowerCase(), dias_a_deadline: null, source: "manual" };
  return { nivel: "baja", dias_a_deadline: null, source: "default" };
}

/**
 * Colores de badge por estado (normalizado). Cubre vocabulario Monday + legacy.
 * El LABEL que se muestra es siempre el estado EXACTO (sin tocar).
 */
const ESTADO_CLS = {
  // Monday — activos
  "brief pendiente":          "bg-muted text-foreground",
  "para asignar":             "bg-muted text-foreground",
  "asignado":                 "bg-[#CFFAFE] text-[#155E75]",
  "en curso":                 "bg-[#EDE9FE] text-[#5B21B6]",
  "para qc":                  "bg-[#EDE9FE] text-[#5B21B6]",
  "qc cambios":               "bg-[#FEF3C7] text-[#92400E]",
  "qc aprobado":              "bg-[#EDE9FE] text-[#5B21B6]",
  "ec cambios":               "bg-[#FEF3C7] text-[#92400E]",
  "ec aprobado":              "bg-[#CFFAFE] text-[#155E75]",
  "cliente cambios":          "bg-[#FEF3C7] text-[#92400E]",
  "ajustar brief":            "bg-[#FEF3C7] text-[#92400E]",
  "pendiente de produccion":  "bg-[#CFFAFE] text-[#155E75]",
  // Monday — por publicar
  "aprobado cliente":         "bg-[#D1FAE5] text-[#065F46]",
  "programado":               "bg-[#CFFAFE] text-[#155E75]",
  // Monday — cerrado
  "publicado":                "bg-[#EDE9FE] text-[#5B21B6]",
  "entregado":                "bg-[#D1FAE5] text-[#065F46]",
  // pausa / cancelado / archivado
  "pausa":                    "bg-muted text-muted-foreground",
  "cancelado":                "bg-muted text-muted-foreground",
  "archivado":                "bg-muted text-muted-foreground",
  // Legacy internos (compat)
  "pendiente":         "bg-muted text-foreground",
  "borrador":          "bg-muted text-foreground",
  "brief":             "bg-[#CFFAFE] text-[#155E75]",
  "en-proceso":        "bg-[#EDE9FE] text-[#5B21B6]",
  "revision-interna":  "bg-[#EDE9FE] text-[#5B21B6]",
  "revision-ec":       "bg-[#EDE9FE] text-[#5B21B6]",
  "revision-cliente":  "bg-[#FEF3C7] text-[#92400E]",
  "aprobado-cliente":  "bg-[#D1FAE5] text-[#065F46]",
  "aprobado":          "bg-[#D1FAE5] text-[#065F46]",
  "publicado-sin-aprobacion": "bg-[#EDE9FE] text-[#5B21B6]",
  "ec-entregado":      "bg-[#CFFAFE] text-[#155E75]",
};

/**
 * Badge de estado universal. Devuelve { label, cls }.
 * label = estado EXACTO de Monday (no se transforma); cls = color.
 */
export function estadoBadge(estado) {
  return {
    label: estado || "—",
    cls:   ESTADO_CLS[normEstado(estado)] || "bg-muted text-muted-foreground",
  };
}

/**
 * Fecha efectiva canónica — v5.5 LEGACY (preferir fechaPlanificada / fechaLimiteInterna / fechaDeadline).
 * Cascada: fecha_tarea → fecha_limite → deadline → fecha_publicacion → fecha_entrega.
 */
export function fechaEfectiva(t) {
  if (!t) return null;
  const raw = t.fecha_tarea || t.fecha_limite || t.deadline || t.fecha_publicacion || t.fecha_entrega;
  if (!raw) return null;
  return String(raw).slice(0, 10);
}

/** HOY en zona Paraguay (UTC-3) YYYY-MM-DD. */
export function hoyPY() {
  return new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
}

// v8.6.22 · Hora de entrega (deadline) — la sesión de Postgres corre en UTC,
// así que un datetime sin offset explícito se guardaría literal como UTC (3h
// antes de lo que alguien tipeó en Paraguay). Estos dos helpers son el único
// lugar donde se hace esa conversión — todo componente que combine o muestre
// la hora de un `deadline` debe pasar por acá, no reimplementar el offset a
// mano (bug real encontrado: la tabla de Tareas editaba el deadline con un
// <input type="date"> suelto que mandaba solo la fecha sin hora, pisando en
// silencio cualquier hora ya cargada — quedaba en medianoche UTC).
/** Combina fecha (YYYY-MM-DD) + hora (HH:MM, opcional) en un ISO con offset PY fijo. Sin hora → 18:00 (fin de jornada). */
export function combinarFechaHoraPY(fecha, hora) {
  if (!fecha) return null;
  return `${fecha}T${hora || "18:00"}:00-03:00`;
}

/** Extrae la hora (HH:MM) en zona Paraguay de un timestamp UTC (ISO string). "" si no hay valor. */
export function horaPY(isoUtc) {
  if (!isoUtc) return "";
  const d = new Date(isoUtc);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - 3 * 3600 * 1000).toISOString().slice(11, 16);
}

/** Extrae la fecha (YYYY-MM-DD) en zona Paraguay de un timestamp UTC (ISO string). "" si no hay valor. */
export function fechaPY(isoUtc) {
  if (!isoUtc) return "";
  const d = new Date(isoUtc);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - 3 * 3600 * 1000).toISOString().slice(0, 10);
}

/**
 * Extrae fecha YYYY-MM-DD del prefijo del nombre de tarea.
 * Convención del Crew: "YYYYMMDD_Cliente_Descripción"
 *   "20260512_La Consolidada_BatallaSabores" → "2026-05-12"
 *   "Tarea sin prefijo"                       → null
 *
 * Usado para ordenar tareas dentro de cada grupo cronológicamente
 * (más temprano primero) sin depender de campos de fecha que pueden estar vacíos.
 */
export function fechaFromNombre(nombre) {
  if (!nombre) return null;
  const m = String(nombre).match(/^(\d{4})(\d{2})(\d{2})[\s_-]/);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/**
 * Comparador para ordenar tareas cronológicamente (más temprano primero).
 * Usa fechaFromNombre como primario, fechaEfectiva como fallback.
 *   sortTareasByFecha(a, b) → -1 si a más vieja, 1 si más nueva
 */
export function sortTareasByFecha(a, b) {
  const fa = fechaFromNombre(a.nombre) || fechaEfectiva(a) || "9999-12-31";
  const fb = fechaFromNombre(b.nombre) || fechaEfectiva(b) || "9999-12-31";
  if (fa === fb) {
    // Empate por fecha → ordenar alfabético por nombre (estable)
    return (a.nombre || "").localeCompare(b.nombre || "");
  }
  return fa.localeCompare(fb);   // ASC: más vieja primero
}

/**
 * Is a date overdue? — admite dos formas:
 *   isOverdue(tarea)          → usa fechaEfectiva(tarea) (recomendado)
 *   isOverdue(dateStr, estado) → forma legacy mantenida por compat
 *
 * SOLO tareas activas con fecha vencida.
 */
export function isOverdue(arg, estadoLegacy) {
  // v5.5: SOLO fecha_limite (deadline interno) marca atrasada.
  // v8.4.24 · Cualquier estado en ESTADOS_AVANCE ya representa un pase
  //   efectivo — no debe reportarse como atrasado aunque la fecha pase.
  if (arg && typeof arg === "object") {
    if (!isActiva(arg.estado)) return false;
    if (isEnAvance(arg.estado)) return false;
    const f = fechaLimiteInterna(arg);
    if (!f) return false;
    return f < hoyPY();
  }
  // Forma legacy: recibe string + estado
  if (!arg) return false;
  if (!isActiva(estadoLegacy)) return false;
  if (isEnAvance(estadoLegacy)) return false;
  return new Date(arg + "T23:59:59") < new Date();
}

/** Initials from full name */
export function initials(name = "") {
  return name.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");
}

/**
 * Pipeline de estados — EXACTO Monday (orden = flujo natural del tablero).
 * Estos son los 18 estados reales de los tableros de Gestión de Contenido.
 */
export const ESTADOS_FLOW = [
  "Brief Pendiente",
  "Para Asignar",
  "Asignado",
  "En Curso",
  "Ajustar Brief",
  "Para QC",
  "QC Cambios",
  "QC Aprobado",
  "EC Cambios",
  "EC Aprobado",
  "Enviado Cliente",                            // jun 2026 v4
  "Tarea con Cambio",                           // jun 2026 v4 — NUEVO
  "Cliente Cambios",
  "Pendiente de Producción",
  "Aprobado Cliente",
  "Programado",
  "Publicado",
  "Terminado",                                  // jun 2026 v4
  "Entregado",
  "Pausa",
  "Cancelado",
];

/**
 * Estado → familia de color Tailwind — FUENTE ÚNICA DE VERDAD.
 * Regla: cada estado tiene su propio hue, NUNCA se repite entre estados
 * (antes "QC Cambios"/"EC Cambios"/"Ajustar Brief" compartían orange, y
 * "Enviado Cliente"/"Pendiente de Producción" compartían cyan — bug real
 * reportado en vivo, ago 2026). Si agregás un estado nuevo, agregale un
 * hue de esta lista que todavía no esté usado abajo.
 */
const ESTADO_HUE = {
  "Brief Pendiente":          "slate",
  "Para Asignar":             "gray",
  "Asignado":                 "blue",
  "En Curso":                 "indigo",
  "Ajustar Brief":            "red",
  "Para QC":                  "purple",
  "QC Cambios":               "orange",
  "QC Aprobado":              "violet",
  "EC Cambios":               "amber",
  "EC Aprobado":              "sky",
  "Enviado Cliente":          "cyan",
  "Tarea con Cambio":         "rose",
  "Cliente Cambios":          "yellow",
  "Pendiente de Producción":  "teal",
  "Aprobado Cliente":         "lime",
  "Programado":               "pink",
  "Publicado":                "fuchsia",
  "Terminado":                "emerald",
  "Entregado":                "green",
  "Pausa":                    "zinc",
  "Cancelado":                "stone",
};

/** Estado → color class (badge claro). Keys = estado EXACTO de Monday. */
export const ESTADO_COLORS = {
  "Brief Pendiente":         "bg-[#F1F5F9] text-[#334155]",
  "Para Asignar":            "bg-[#F3F4F6] text-[#374151]",
  "Asignado":                "bg-[#DBEAFE] text-[#1E40AF]",
  "En Curso":                "bg-[#E0E7FF] text-[#3730A3]",
  "Ajustar Brief":           "bg-[#FEE2E2] text-[#991B1B]",
  "Para QC":                 "bg-[#FCE7F3] text-[#9D174D]",
  "QC Cambios":              "bg-[#FFEDD5] text-[#9A3412]",
  "QC Aprobado":             "bg-[#EDE9FE] text-[#5B21B6]",
  "EC Cambios":              "bg-[#FED7AA] text-[#7C2D12]",
  "EC Aprobado":             "bg-[#E0F2FE] text-[#075985]",
  "Enviado Cliente":         "bg-[#CFE0F4] text-[#003F7D]",
  "Tarea con Cambio":        "bg-[#FFE4E6] text-[#9F1239]",
  "Cliente Cambios":         "bg-[#FEF9C3] text-[#854D0E]",
  "Pendiente de Producción": "bg-[#E5E7EB] text-[#1F2937]",
  "Aprobado Cliente":        "bg-[#ECFCCB] text-[#3F6212]",
  "Programado":              "bg-[#FBCFE8] text-[#831843]",
  "Publicado":               "bg-[#FAE8FF] text-[#86198F]",
  "Terminado":               "bg-[#D1FAE5] text-[#065F46]",
  "Entregado":               "bg-[#DCFCE7] text-[#166534]",
  "Pausa":                   "bg-[#F4F4F5] text-[#52525B]",
  "Cancelado":               "bg-[#F5F5F4] text-[#78716C]",
};

/* Colores de estado · FUENTE ÚNICA.
 * Son los del brand book (README, «Estados (no cambiar)»), con los 4 ajustes pedidos el 12-oct:
 * Para QC rosado, EC Cambios naranja más fuerte, Enviado Cliente azul Francia, Pendiente de Producción gris.
 * Regla: ningún estado comparte color con otro. La unificación de paleta del 11-oct los había aplastado a cinco
 * matices y varios quedaron iguales; si agregás un estado nuevo, dale un color propio acá (y en los 4 mapas). */
export const ESTADO_DOT = {
  "Brief Pendiente":         "bg-[#64748B]",
  "Para Asignar":            "bg-[#9CA3AF]",
  "Asignado":                "bg-[#2563EB]",
  "En Curso":                "bg-[#4F46E5]",
  "Ajustar Brief":           "bg-[#DC2626]",
  "Para QC":                 "bg-[#F472B6]",
  "QC Cambios":              "bg-[#EA580C]",
  "QC Aprobado":             "bg-[#7C3AED]",
  "EC Cambios":              "bg-[#C2410C]",
  "EC Aprobado":             "bg-[#0284C7]",
  "Enviado Cliente":         "bg-[#0055A4]",
  "Tarea con Cambio":        "bg-[#E11D48]",
  "Cliente Cambios":         "bg-[#EAB308]",
  "Pendiente de Producción": "bg-[#374151]",
  "Aprobado Cliente":        "bg-[#65A30D]",
  "Programado":              "bg-[#DB2777]",
  "Publicado":               "bg-[#A21CAF]",
  "Terminado":               "bg-[#047857]",
  "Entregado":               "bg-[#16A34A]",
  "Pausa":                   "bg-[#A1A1AA]",
  "Cancelado":               "bg-[#A8A29E]",
};

export const ESTADO_BORDER_L = {
  "Brief Pendiente":         "border-l-[#64748B]",
  "Para Asignar":            "border-l-[#9CA3AF]",
  "Asignado":                "border-l-[#2563EB]",
  "En Curso":                "border-l-[#4F46E5]",
  "Ajustar Brief":           "border-l-[#DC2626]",
  "Para QC":                 "border-l-[#F472B6]",
  "QC Cambios":              "border-l-[#EA580C]",
  "QC Aprobado":             "border-l-[#7C3AED]",
  "EC Cambios":              "border-l-[#C2410C]",
  "EC Aprobado":             "border-l-[#0284C7]",
  "Enviado Cliente":         "border-l-[#0055A4]",
  "Tarea con Cambio":        "border-l-[#E11D48]",
  "Cliente Cambios":         "border-l-[#EAB308]",
  "Pendiente de Producción": "border-l-[#374151]",
  "Aprobado Cliente":        "border-l-[#65A30D]",
  "Programado":              "border-l-[#DB2777]",
  "Publicado":               "border-l-[#A21CAF]",
  "Terminado":               "border-l-[#047857]",
  "Entregado":               "border-l-[#16A34A]",
  "Pausa":                   "border-l-[#A1A1AA]",
  "Cancelado":               "border-l-[#A8A29E]",
};

/** Estado → celda con FONDO COMPLETO color (estilo Monday). Keys = estado EXACTO. */
export const ESTADO_CELL = {
  "Brief Pendiente":         "bg-[#64748B] text-white",
  "Para Asignar":            "bg-[#9CA3AF] text-white",
  "Asignado":                "bg-[#2563EB] text-white",
  "En Curso":                "bg-[#4F46E5] text-white",
  "Ajustar Brief":           "bg-[#DC2626] text-white",
  "Para QC":                 "bg-[#F9A8D4] text-[#831843]",
  "QC Cambios":              "bg-[#EA580C] text-white",
  "QC Aprobado":             "bg-[#7C3AED] text-white",
  "EC Cambios":              "bg-[#C2410C] text-white",
  "EC Aprobado":             "bg-[#0284C7] text-white",
  "Enviado Cliente":         "bg-[#0055A4] text-white",
  "Tarea con Cambio":        "bg-[#E11D48] text-white",
  "Cliente Cambios":         "bg-[#EAB308] text-white",
  "Pendiente de Producción": "bg-[#374151] text-white",
  "Aprobado Cliente":        "bg-[#65A30D] text-white",
  "Programado":              "bg-[#DB2777] text-white",
  "Publicado":               "bg-[#A21CAF] text-white",
  "Terminado":               "bg-[#047857] text-white",
  "Entregado":               "bg-[#16A34A] text-white",
  "Pausa":                   "bg-[#E4E4E7] text-[#3F3F46]",
  "Cancelado":               "bg-[#E7E5E4] text-[#57534E]",
};

/** Estado → label. EXACTO de Monday (no se transforma). */
export const ESTADO_LABEL = {
  "Brief Pendiente":          "Brief Pendiente",
  "Para Asignar":             "Para Asignar",
  "Asignado":                 "Asignado",
  "En Curso":                 "En Curso",
  "Ajustar Brief":            "Ajustar Brief",
  "Para QC":                  "Para QC",
  "QC Cambios":               "QC Cambios",
  "QC Aprobado":              "QC Aprobado",
  "EC Cambios":               "EC Cambios",
  "EC Aprobado":              "EC Aprobado",
  "Enviado Cliente":          "Enviado Cliente",     // jun 2026 v4
  "Tarea con Cambio":         "Tarea con Cambio",    // jun 2026 v4
  "Cliente Cambios":          "Cliente Cambios",
  "Pendiente de Producción":  "Pendiente de Producción",
  "Aprobado Cliente":         "Aprobado Cliente",
  "Programado":               "Programado",
  "Publicado":                "Publicado",
  "Terminado":                "Terminado",            // jun 2026 v4
  "Entregado":                "Entregado",
  "Pausa":                    "Pausa",
  "Cancelado":                "Cancelado",
};

/**
 * Gestión del Tiempo — derivado de fecha_entrega vs hoy + estado
 * Match con la columna "Gestión del Tiempo" de Monday.
 */
export function getGestionTiempo(tarea) {
  const e = normEstado(tarea.estado);
  // "aprobado-cliente"/aprobado/entregado/publicado/archivado NO están vencidos.
  if (isListo(e)) {
    // "Listo" pero todavía sin publicar → distinción visual
    // «Por publicar» solo si tiene fecha de publicación; sin fecha no requiere publicarse y se da por finalizada.
    if (isPendientePublicar(e) && tarea.fecha_publicacion) {
      return { value: "pendiente-publicar", label: "Por publicar", cls: "bg-[#059669] text-white" };
    }
    return { value: "completado", label: "Completado", cls: "bg-[#065F46] text-white" };
  }
  // EC Aprobado: el trabajo está hecho, solo falta envío al cliente.
  // Se delega al chip "estado de envío" (jun 2026 v5.2). Acá NO mostramos vencido.
  if (tarea.estado === "EC Aprobado") {
    return { value: "ec-aprobado", label: "Pend. envío", cls: "bg-[#155E75] text-white" };
  }
  // SOA 10 PRO · bug 1: era zinc-700 sobre zinc-700 (texto invisible) → #E4E4E7 / #3F3F46.
  if (e === "pausa")     return { value: "pausa",     label: "Pausa",     cls: "bg-[#E4E4E7] text-[#3F3F46]" };
  if (e === "cancelado") return { value: "cancelado", label: "Cancelado", cls: "bg-secondary text-muted-foreground" };
  // Solo tareas ACTIVAS pueden estar vencidas.
  // Definición ÚNICA de "vencida": isOverdue() (fecha_limite < hoy, activa, sin pase efectivo a otro rol).
  // La píldora, el KPI de Tareas, Inicio y Kanban deben coincidir; antes la píldora usaba la cascada de
  // fechas (fecha_tarea primero) y contaba "Vencido" donde el KPI contaba 1 de 5.
  if (isEnAvance(e)) return { value: "en-tiempo", label: "En tiempo", cls: "bg-[#065F46] text-white" };
  const fecha = fechaEfectiva(tarea);
  if (!fecha) {
    return { value: "sin-fecha", label: "Sin fecha", cls: "bg-muted text-muted-foreground" };
  }
  // TZ Paraguay: `today` debe ser el día PY a medianoche, no la TZ local del browser
  // (que en el server o en clientes con otras TZ daría días distintos).
  const today = new Date(hoyPY() + "T00:00:00-03:00");
  const due   = new Date(fecha + "T23:59:59-03:00");
  const diff  = (due - today) / (1000 * 60 * 60 * 24);

  if (isOverdue(tarea)) return { value: "vencido", label: "Vencido",  cls: "bg-[#B91C1C] text-white" };
  if (diff < 1)  return { value: "hoy",     label: "Hoy",      cls: "bg-[#92400E] text-white" };  // incluye fecha planificada ya pasada sin límite interno vencido
  if (diff < 3)  return { value: "pronto",  label: "Pronto",   cls: "bg-[#92400E] text-white" };
  return { value: "en-tiempo", label: "En tiempo", cls: "bg-[#065F46] text-white" };
}

/** Format datetime → "abr. 21, 2:30 PM" */
export function formatDateTime(str) {
  if (!str) return "—";
  try {
    const d = new Date(str.includes("T") ? str : str + "T12:00:00");
    return d.toLocaleDateString("es-PY", {
      day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return str; }
}

/** Format elapsed seconds → "1h 6m 59s" */
export function formatElapsed(seconds) {
  if (!seconds || seconds <= 0) return "0m 0s";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}

/** Prioridad → celda con fondo color */
/* SOA 10 PRO · píldora con fondo suave (handoff README → Prioridad suave) */
export const PRIORIDAD_CELL = {
  urgente: "bg-[#FEE2E2] text-[#B91C1C]",
  alta:    "bg-[#FFEDD5] text-[#C2410C]",
  media:   "bg-[#FEF9C3] text-[#854D0E]",
  baja:    "bg-[#F1F5F9] text-[#475569]",
};

/** Prioridad sólida (puntos/indicadores pequeños). */
export const PRIORIDAD_SOLIDA = {
  urgente: "bg-[#DC2626]", alta: "bg-[#EA580C]", media: "bg-[#CA8A04]", baja: "bg-[#475569]",
};

/** Paleta para colorear grupos (cycle by index) */
export const GROUP_COLORS = [
  { strip: "bg-[#10B981]", text: "text-[#059669]", bg: "bg-[#10B981]/5",  border: "border-[#10B981]/30"  },
  { strip: "bg-[#0E7490]",    text: "text-[#0E7490]",    bg: "bg-[#0E7490]/5",     border: "border-[#0E7490]/30"     },
  { strip: "bg-[#8B5CF6]",  text: "text-[#7C3AED]",  bg: "bg-[#8B5CF6]/5",   border: "border-[#8B5CF6]/30"   },
  { strip: "bg-[#8B5CF6]",    text: "text-[#7C3AED]",    bg: "bg-[#8B5CF6]/5",     border: "border-[#8B5CF6]/30"     },
  { strip: "bg-[#F59E0B]",   text: "text-[#92400E]",   bg: "bg-[#F59E0B]/5",    border: "border-[#F59E0B]/30"    },
  { strip: "bg-[#0E7490]",    text: "text-[#155E75]",    bg: "bg-[#0E7490]/5",     border: "border-[#0E7490]/30"     },
  // SOA 10 PRO · bug 4: indigo-400 / rose-400 no contrastaban sobre blanco → tono oscuro del grupo.
  { strip: "bg-[#8B5CF6]",  text: "text-[#5B21B6]",  bg: "bg-[#8B5CF6]/5",   border: "border-[#8B5CF6]/30"   },
  { strip: "bg-[#DC2626]",    text: "text-[#B91C1C]",    bg: "bg-[#DC2626]/5",     border: "border-[#DC2626]/30"     },
];

// v8.3.8 · Nomenclatura unificada. Los alias legacy se mapean al canónico
// (útil si aparecen valores viejos en tareas no migradas o clientes con cache).
export const TIPO_EMOJI = {
  "Reel":         "🎬",
  "Gráfica":      "🎨",
  "Carrusel":     "🖼️",
  "Historia":     "📱",
  "Fotos":        "📸",
  "Cobertura":    "🎤",
  "Documento":    "📝",
  "Presentación": "📋",
  // Alias legacy → mismo emoji que el canónico correspondiente
  "Historias":               "📱",
  "Video":                   "🎬",
  "Imagen Estática":         "🎨",
  "Presentación/Documento":  "📋",
  "Artículo":                "📝",
  "Infografía":              "🎨",
  "Anuncio (Ads)":           "🎨",
  "UGC":                     "📸",
  "Meme / Tendencia":        "🎨",
  "Newsletter":              "📝",
  "Cartelería":              "🎨",
};

export const PRIORIDAD_COLORS = {
  urgente: "text-[#DC2626]",
  alta:    "text-[#D97706]",
  media:   "text-[#F59E0B]",
  baja:    "text-muted-foreground",
};

export const SEMAFORO_CLASSES = {
  verde:    { text: "text-[#059669]", bg: "bg-[#34D399]", bar: "bg-[#10B981]" },
  amarillo: { text: "text-[#D97706]",   bg: "bg-[#FBBF24]",   bar: "bg-[#F59E0B]"   },
  rojo:     { text: "text-[#DC2626]",     bg: "bg-[#F87171]",     bar: "bg-[#DC2626]"     },
};
